import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import * as monthUtils from 'loot-core/shared/months';
import { integerToCurrency } from 'loot-core/shared/util';
import type { IntegerAmount } from 'loot-core/shared/util';

import { MobilePageHeader, PageHeader } from '@desktop-client/components/Page';
import { useBudgetMonthData } from '@desktop-client/hooks/useBudgetMonthData';

function getMonthProgress(): number {
  const now = new Date();
  const daysInMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
  ).getDate();
  return now.getDate() / daysInMonth;
}

function getSpendingStatus(
  spent: IntegerAmount,
  budgeted: IntegerAmount,
  monthProgress: number,
): 'on-track' | 'caution' | 'overspent' | 'no-budget' {
  if (budgeted === 0) return 'no-budget';
  const spentRatio = -spent / budgeted;
  if (spentRatio > 1) return 'overspent';
  if (spentRatio > monthProgress + 0.1) return 'caution';
  return 'on-track';
}

function getStatusColor(
  status: 'on-track' | 'caution' | 'overspent' | 'no-budget',
): string {
  switch (status) {
    case 'on-track':
      return theme.noticeTextLight;
    case 'caution':
      return theme.warningText;
    case 'overspent':
      return theme.errorText;
    case 'no-budget':
      return theme.pageTextSubdued;
    default:
      return theme.pageTextSubdued;
  }
}

type ProgressBarProps = {
  spent: IntegerAmount;
  budgeted: IntegerAmount;
  monthProgress: number;
};

function ProgressBar({ spent, budgeted, monthProgress }: ProgressBarProps) {
  const spentPositive = -spent;
  const percentage = budgeted > 0 ? Math.min(spentPositive / budgeted, 1.5) : 0;
  const displayPercentage = Math.min(percentage, 1);
  const status = getSpendingStatus(spent, budgeted, monthProgress);
  const color = getStatusColor(status);

  return (
    <View style={{ flex: 1, gap: 4 }}>
      <View
        style={{
          height: 8,
          backgroundColor: theme.tableRowBackgroundHover,
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <View
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${displayPercentage * 100}%`,
            backgroundColor: color,
            borderRadius: 4,
            transition: 'width 0.3s ease',
          }}
        />
        <View
          style={{
            position: 'absolute',
            left: `${monthProgress * 100}%`,
            top: -2,
            bottom: -2,
            width: 2,
            backgroundColor: theme.pageTextSubdued,
            opacity: 0.5,
          }}
        />
      </View>
    </View>
  );
}

type SummaryCardProps = {
  label: string;
  amount: IntegerAmount;
  color?: string;
  compact?: boolean;
};

function SummaryCard({ label, amount, color, compact }: SummaryCardProps) {
  return (
    <div
      style={{
        flex: '1 1 0',
        padding: compact ? 12 : 16,
        backgroundColor: theme.cardBackground,
        borderRadius: 8,
        border: `1px solid ${theme.tableBorder}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Text
        style={{ fontSize: compact ? 11 : 12, color: theme.pageTextSubdued }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontSize: compact ? 18 : 22,
          fontWeight: 600,
          color: color || theme.pageText,
          ...styles.tnum,
        }}
      >
        {integerToCurrency(amount)}
      </Text>
    </div>
  );
}

type CategoryRowProps = {
  name: string;
  spent: IntegerAmount;
  budgeted: IntegerAmount;
  monthProgress: number;
  isLast: boolean;
  compact: boolean;
};

function CategoryRow({
  name,
  spent,
  budgeted,
  monthProgress,
  isLast,
  compact,
}: CategoryRowProps) {
  const status = getSpendingStatus(spent, budgeted, monthProgress);

  if (compact) {
    return (
      <View
        style={{
          padding: '8px 12px',
          gap: 6,
          borderBottom: !isLast ? `1px solid ${theme.tableBorder}` : undefined,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 13,
              color: theme.pageText,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {name}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              gap: 3,
              flexShrink: 0,
              marginLeft: 8,
            }}
          >
            <Text
              style={{
                fontSize: 12,
                color: getStatusColor(status),
                fontWeight: 500,
                ...styles.tnum,
              }}
            >
              {integerToCurrency(-spent)}
            </Text>
            <Text
              style={{
                fontSize: 12,
                color: theme.pageTextSubdued,
                ...styles.tnum,
              }}
            >
              / {integerToCurrency(budgeted)}
            </Text>
          </View>
        </View>
        <ProgressBar
          spent={spent}
          budgeted={budgeted}
          monthProgress={monthProgress}
        />
      </View>
    );
  }

  return (
    <View
      className={css({
        '&:hover': {
          backgroundColor: theme.tableRowBackgroundHover,
        },
      })}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: '10px 16px',
        gap: 16,
        borderBottom: !isLast ? `1px solid ${theme.tableBorder}` : undefined,
      }}
    >
      <Text
        style={{
          width: 160,
          fontSize: 13,
          color: theme.pageText,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Text>

      <ProgressBar
        spent={spent}
        budgeted={budgeted}
        monthProgress={monthProgress}
      />

      <View
        style={{
          width: 120,
          textAlign: 'right',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        <Text
          style={{
            fontSize: 13,
            color: getStatusColor(status),
            fontWeight: 500,
            ...styles.tnum,
          }}
        >
          {integerToCurrency(-spent)}
        </Text>
        <Text
          style={{
            fontSize: 13,
            color: theme.pageTextSubdued,
            ...styles.tnum,
          }}
        >
          {' / '}
          {integerToCurrency(budgeted)}
        </Text>
      </View>
    </View>
  );
}

export function BudgetOverview() {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const currentMonth = monthUtils.currentMonth();
  const [month] = useState(currentMonth);
  const data = useBudgetMonthData(month);
  const monthProgress = getMonthProgress();

  const monthName = monthUtils.format(month, 'MMMM yyyy');
  const dayOfMonth = new Date().getDate();
  const daysInMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0,
  ).getDate();

  const compact = isNarrowWidth;
  const contentPadding = compact ? 10 : 20;

  return (
    <View
      style={{
        ...(compact
          ? { backgroundColor: theme.mobilePageBackground }
          : { ...styles.page, backgroundColor: theme.pageBackground }),
        flex: 1,
      }}
    >
      {compact ? (
        <MobilePageHeader title={t('Overview') + ' \u2014 ' + monthName} />
      ) : (
        <PageHeader
          title={
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'baseline',
                gap: 12,
              }}
            >
              <Trans>Budget Overview</Trans>
              <Text
                style={{
                  fontSize: 14,
                  color: theme.pageTextSubdued,
                  fontWeight: 400,
                }}
              >
                {monthName}
              </Text>
            </View>
          }
        />
      )}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: `0 ${contentPadding}px 40px ${contentPadding}px`,
        }}
      >
        <div
          style={{
            maxWidth: compact ? undefined : 900,
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? 12 : 24,
          }}
        >
          {/* Month progress */}
          <View
            style={{
              padding: compact ? 12 : 16,
              backgroundColor: theme.cardBackground,
              borderRadius: 8,
              border: `1px solid ${theme.tableBorder}`,
              gap: 8,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                <Trans>Month Progress</Trans>
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  color: theme.pageTextSubdued,
                  ...styles.tnum,
                }}
              >
                {t('Day {{current}} of {{total}}', {
                  current: dayOfMonth,
                  total: daysInMonth,
                })}
              </Text>
            </View>
            <View
              style={{
                height: 6,
                backgroundColor: theme.tableRowBackgroundHover,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <View
                style={{
                  height: '100%',
                  width: `${monthProgress * 100}%`,
                  backgroundColor: theme.pageTextSubdued,
                  borderRadius: 3,
                  transition: 'width 0.3s ease',
                }}
              />
            </View>
          </View>

          {/* Summary cards */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: compact ? 8 : 12,
            }}
          >
            <SummaryCard
              label={t('Budgeted')}
              amount={data.totalBudgeted}
              compact={compact}
            />
            <SummaryCard
              label={t('Spent')}
              amount={data.totalSpent}
              color={theme.errorText}
              compact={compact}
            />
            <SummaryCard
              label={t('Left')}
              amount={data.totalBalance}
              color={
                data.totalBalance >= 0 ? theme.noticeTextLight : theme.errorText
              }
              compact={compact}
            />
          </div>

          {/* Category groups */}
          {data.groups.map(groupData => {
            const groupStatus = getSpendingStatus(
              groupData.totalSpent,
              groupData.totalBudgeted,
              monthProgress,
            );
            return (
              <View
                key={groupData.group.id}
                style={{
                  backgroundColor: theme.cardBackground,
                  borderRadius: 8,
                  border: `1px solid ${theme.tableBorder}`,
                  overflow: 'hidden',
                }}
              >
                {/* Group header */}
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: compact ? '10px 12px' : '12px 16px',
                    borderBottom: `1px solid ${theme.tableBorder}`,
                    backgroundColor: theme.tableHeaderBackground,
                  }}
                >
                  <Text
                    style={{
                      fontWeight: 600,
                      fontSize: compact ? 13 : 14,
                      color: theme.pageText,
                    }}
                  >
                    {groupData.group.name}
                  </Text>
                  <Text
                    style={{
                      fontSize: compact ? 12 : 13,
                      color: getStatusColor(groupStatus),
                      fontWeight: 500,
                      ...styles.tnum,
                    }}
                  >
                    {integerToCurrency(-groupData.totalSpent)}
                    {' / '}
                    {integerToCurrency(groupData.totalBudgeted)}
                  </Text>
                </View>

                {/* Category rows */}
                {groupData.categories.map((catData, index) => (
                  <CategoryRow
                    key={catData.category.id}
                    name={catData.category.name}
                    spent={catData.spent}
                    budgeted={catData.budgeted}
                    monthProgress={monthProgress}
                    isLast={index === groupData.categories.length - 1}
                    compact={compact}
                  />
                ))}
              </View>
            );
          })}
        </div>
      </div>
    </View>
  );
}
